import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronUp,
  Coins,
  Crown,
  Gem,
  Medal,
  Percent,
  RotateCw,
  Target,
  Timer,
  TrendingUp,
  Trophy,
  Users,
  Zap
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { eventApi } from '../../../services'
import useAppStore from '../../../store/useAppStore'

const EventPage = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { user } = useAppStore()

  // Флаг активности ивента (пока хардкод)
  const isEventActive = false

  const [eventData, setEventData] = useState(null)
  const [secondsLeft, setSecondsLeft] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedCategories, setExpandedCategories] = useState({})

  useEffect(() => {
    if (!isEventActive) {
      navigate('/', { replace: true })
    }
  }, [isEventActive, navigate])

  useEffect(() => {
    let isMounted = true

    const loadEvent = async () => {
      try {
        setIsLoading(true)
        setError(null)
        if (!isEventActive) return
        const data = await eventApi.getEventData()

        if (data?.error) {
          toast.error(
            t('eventOlympus.errorBackend', {
              defaultValue: 'Ошибка сервера. Попробуйте позже.'
            })
          )
          navigate('/', { replace: true })
          return
        }

        if (isMounted) {
          setEventData(data)
          const parsedSecondsLeft = Number(data?.event?.second_to_finish ?? 0)
          setSecondsLeft(Number.isFinite(parsedSecondsLeft) ? parsedSecondsLeft : 0)
        }
      } catch (err) {
        if (isMounted) {
          setError(
            t('eventOlympus.error', {
              defaultValue: 'Не удалось загрузить данные ивента'
            })
          )
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadEvent()

    return () => {
      isMounted = false
    }
  }, [isEventActive, navigate, t])

  useEffect(() => {
    if (!isEventActive) return
    const intervalId = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev === null) return prev
        return prev > 0 ? prev - 1 : 0
      })
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [isEventActive])

  const eventRoot = eventData?.event
  const random100Value = eventRoot?.random_100
  const hasRandom100 = typeof random100Value === 'boolean'
  const isRandom100Participant = random100Value === true
  const topRoot = eventRoot?.top || {}

  const normalizeTopList = (top) => (Array.isArray(top) ? top : Object.values(top || {}))
  const formatNumber = useCallback(
    (value) => Number(value || 0).toLocaleString(i18n.language || 'ru-RU'),
    [i18n.language]
  )

  const formatCountdown = (totalSeconds) => {
    const safeSeconds = Math.max(0, Number(totalSeconds || 0))
    const days = Math.floor(safeSeconds / 86400)
    const hours = Math.floor((safeSeconds % 86400) / 3600)
    const minutes = Math.floor((safeSeconds % 3600) / 60)
    const seconds = safeSeconds % 60
    const pad = (num) => String(num).padStart(2, '0')

    if (days > 0) {
      return `${days}д ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    }

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  }

  const formatChance = useCallback((chance) => {
    const numericChance = Number(chance)
    if (!Number.isFinite(numericChance)) return '—'
    return `${numericChance.toFixed(2)}%`
  }, [])

  const categories = useMemo(
    () => [
      {
        key: 'highroller',
        title: t('eventOlympus.categories.highroller.title', { defaultValue: 'Хайроллер' }),
        icon: Coins,
        description: t('eventOlympus.categories.highroller.desc', {
          defaultValue: 'Сумма всех ставок за период Штурма Олимпа.'
        }),
        metricLabel: t('eventOlympus.categories.highroller.metric', { defaultValue: 'Сумма ставок' }),
        metricIcon: Gem,
        metricKey: 'sum',
        formatValue: (entry) => formatNumber(entry?.sum)
      },
      {
        key: 'marathon_runner',
        title: t('eventOlympus.categories.marathon.title', { defaultValue: 'Марафонец' }),
        icon: TrendingUp,
        description: t('eventOlympus.categories.marathon.desc', {
          defaultValue: 'Общее количество участий в битвах.'
        }),
        metricLabel: t('eventOlympus.categories.marathon.metric', { defaultValue: 'Битв' }),
        metricIcon: Zap,
        metricKey: 'unique_rounds',
        formatValue: (entry) => formatNumber(entry?.unique_rounds)
      },
      {
        key: 'legend_1_percent',
        title: t('eventOlympus.categories.legend.title', { defaultValue: '1% Легенда' }),
        icon: Percent,
        description: t('eventOlympus.categories.legend.desc', {
          defaultValue: 'Самый большой банк при шансе победителя ≤ 1%.'
        }),
        metricLabel: t('eventOlympus.categories.legend.metric', { defaultValue: 'Банк' }),
        metricIcon: Trophy,
        metricKey: 'win_sum',
        formatValue: (entry) => formatNumber(entry?.win_sum ?? entry?.profit),
        extraLine: (entry) =>
          t('eventOlympus.categories.legend.extra', {
            defaultValue: 'Шанс: {{chance}}',
            chance: formatChance(entry?.chance)
          })
      },
      {
        key: 'sniper',
        title: t('eventOlympus.categories.sniper.title', { defaultValue: 'Снайпер' }),
        icon: Target,
        description: t('eventOlympus.categories.sniper.desc', {
          defaultValue: 'Максимальный профит в одном раунде.'
        }),
        metricLabel: t('eventOlympus.categories.sniper.metric', { defaultValue: 'Профит' }),
        metricIcon: Medal,
        metricKey: 'max_profit',
        formatValue: (entry) => formatNumber(entry?.max_profit)
      },
      {
        key: 'win_streak',
        title: t('eventOlympus.categories.streak.title', { defaultValue: 'Винстрик' }),
        icon: Crown,
        description: t('eventOlympus.categories.streak.desc', {
          defaultValue: 'Самая длинная серия побед подряд (при равенстве — по чистому профиту в серии).'
        }),
        metricLabel: t('eventOlympus.categories.streak.metric', { defaultValue: 'Побед подряд' }),
        metricIcon: Crown,
        metricKey: 'streak',
        formatValue: (entry) => formatNumber(entry?.streak),
        extraLine: (entry) =>
          t('eventOlympus.categories.streak.extra', {
            defaultValue: 'Профит: {{profit}}',
            profit: formatNumber(entry?.profit)
          })
      }
    ],
    [formatChance, formatNumber, t]
  )
  const userDisplayName = useMemo(() => {
    if (!user) return ''
    const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim()
    return fullName || user.username || user.name || ''
  }, [user])

  const accentsByCategoryKey = {
    highroller: {
      border: 'border-amber-300/30',
      bg: 'bg-amber-500/10',
      text: 'text-amber-200'
    },
    marathon_runner: {
      border: 'border-sky-300/30',
      bg: 'bg-sky-500/10',
      text: 'text-sky-200'
    },
    legend_1_percent: {
      border: 'border-emerald-300/30',
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-200'
    },
    sniper: {
      border: 'border-rose-300/30',
      bg: 'bg-rose-500/10',
      text: 'text-rose-200'
    },
    win_streak: {
      border: 'border-purple-300/30',
      bg: 'bg-purple-500/10',
      text: 'text-purple-200'
    }
  }

  const toggleCategoryExpanded = useCallback((categoryKey) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }))
  }, [])

  const renderCategoryCard = (category) => {
    const accent = accentsByCategoryKey[category.key] || accentsByCategoryKey.highroller
    const categoryData = topRoot?.[category.key] || {}
    const topEntries = normalizeTopList(categoryData?.top).slice(0, 3)
    const topEntriesByPosition = topEntries.reduce((acc, entry) => {
      const pos = Number(entry?.position)
      if (Number.isFinite(pos) && pos >= 1 && pos <= 3) {
        acc[pos] = entry
      }
      return acc
    }, {})
    const prizes = Array.isArray(categoryData?.prize) ? categoryData.prize : Object.values(categoryData?.prize || {})
    const slots = [1, 2, 3].map((position) => {
      const entry = topEntriesByPosition[position]
      return entry || { position, placeholder: true }
    })
    const isExpanded = Boolean(expandedCategories[category.key])
    const visibleTopList = isExpanded ? slots : slots.slice(0, 1)
    const hiddenSlots = slots.slice(1)
    const canExpand = hiddenSlots.some((entry, index) => prizes[index + 1] || !entry?.placeholder)
    const hiddenCount = canExpand ? hiddenSlots.length : 0
    const meData = categoryData?.me
    const userInTop = topEntries.find((entry) => entry?.you)
    const shouldShowUserRow = !userInTop
    const hasMeData = Boolean(meData && meData !== false)
    const shouldShowMeRow = shouldShowUserRow && hasMeData
    const truncateName = (name) => (name && name.length > 18 ? `${name.slice(0, 18)}…` : name)
    const MetricIcon = category.metricIcon

    return (
      <div key={category.key} className="relative min-w-0 overflow-hidden rounded-xl p-[1px]">
        <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-br from-sky-300/45 via-amber-300/25 to-emerald-300/45 opacity-80" />
        <div className="pointer-events-none absolute -inset-1 rounded-2xl bg-gradient-to-br from-sky-400/20 via-amber-300/10 to-emerald-400/20 blur-md" />
        <div className={`relative rounded-[11px] border ${accent.border} bg-surface/80 p-4`}>
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="min-w-0">
              <div className={`text-base font-black leading-snug ${accent.text}`}>{category.title}</div>
              <div className="mt-0.5 text-xs leading-snug text-white/60">{category.description}</div>
            </div>
          </div>
        </div>

        <div className="mt-3 space-y-3">
            {visibleTopList.map((entry, index) => {
              const position = Number(entry?.position ?? index + 1)
              const isPlaceholder = Boolean(entry?.placeholder)
              const isCurrentUser = Boolean(entry?.you) && !isPlaceholder
              const extraText = !isPlaceholder && category.extraLine ? category.extraLine(entry) : null
              const prize = prizes[position - 1]
              const displayName = isPlaceholder
                ? t('eventOlympus.emptyWinner', { defaultValue: 'Пока нет игрока' })
                : entry?.name || ''
              const metricValue = isPlaceholder ? '—' : category.formatValue(entry)

              return (
                <div
                  key={`${category.key}-${position}-${isPlaceholder ? 'placeholder' : entry?.name || index}`}
                  className={`relative rounded-xl border px-3 py-4 ${
                    isCurrentUser
                      ? `${accent.border} ${accent.bg} shadow-[0_0_18px_rgba(0,255,127,0.12)]`
                      : 'border-white/10 bg-dark-primary/40'
                  }`}
                >
                  <div
                    className={`absolute left-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                      position === 1
                        ? 'bg-amber-400/20 text-amber-200'
                        : position === 2
                          ? 'bg-slate-300/20 text-slate-100'
                          : 'bg-orange-500/20 text-orange-200'
                    }`}
                  >
                    {position}
                  </div>

                  <div className="flex flex-col items-center text-center">
                    <div className="h-14 w-14 overflow-hidden rounded-full border border-white/10 bg-dark-primary">
                      {isPlaceholder ? (
                        <div className="flex h-full w-full items-center justify-center">
                          <Users size={22} className="text-white/20" />
                        </div>
                      ) : (
                        <img
                          src={
                            entry?.ava ||
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(entry?.name || 'User')}&background=00FF7F&color=0A0A0F`
                          }
                          alt={entry?.name || 'User'}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                    <div className="mt-2 w-full break-words text-base font-semibold leading-snug text-white">
                      {displayName}
                    </div>
                    {extraText && <div className="mt-1 w-full break-words text-xs text-white/55">{extraText}</div>}
                  </div>

                  <div className="mt-4 flex flex-col items-center gap-0.5 text-center">
                    <div className="flex items-center justify-center gap-1 text-base font-semibold text-accent-primary">
                      <MetricIcon size={14} className="text-accent-primary/80" />
                      {metricValue}
                    </div>
                    <div className="text-[10px] text-white/45 uppercase tracking-[0.12em]">
                      {category.metricLabel}
                    </div>
                  </div>

                  {prize && (
                    <div className="mt-4 rounded-lg bg-surface/40 px-3 py-3 text-center">
                      <img src={prize?.img} alt={prize?.name} className="mx-auto h-12 w-12 rounded-md object-cover" />
                      <div className="mt-2 break-words text-sm font-semibold text-white">{prize?.name}</div>
                      <div className="mt-1 flex items-center justify-center gap-1 text-xs text-white/70">
                        <Gem size={12} className="text-white/50" />
                        {formatNumber(prize?.price)}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            {canExpand && (
              <button
                type="button"
                onClick={() => toggleCategoryExpanded(category.key)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-dark-primary/40 px-3 py-2 text-xs font-semibold text-white/75 transition-colors hover:border-white/20 hover:text-white"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp size={14} />
                    {t('eventOlympus.showLess', { defaultValue: 'Свернуть' })}
                  </>
                ) : (
                  <>
                    <ChevronDown size={14} />
                    {t('eventOlympus.showMoreTop', {
                      defaultValue: 'Показать ещё {{count}}',
                      count: hiddenCount
                    })}
                  </>
                )}
              </button>
            )}
          </div>

        {shouldShowMeRow && (
          <div className="mt-2.5">
            <div className="mb-2 flex items-center justify-center gap-1 py-0.5">
              <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
              <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
              <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
            </div>
            <div className={`rounded-xl border px-3 py-3 ${accent.border} ${accent.bg}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-white/10 bg-dark-primary">
                  <img
                    src={
                      user?.ava ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(userDisplayName || 'User')}&background=00FF7F&color=0A0A0F`
                    }
                    alt={userDisplayName || 'User'}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`truncate text-sm font-semibold ${accent.text}`}>{truncateName(userDisplayName || 'Ты')}</div>
                  <div className="mt-0.5 truncate text-xs text-white/55">
                    {t('eventOlympus.yourPosition', {
                      defaultValue: 'Твоё место: #{{pos}}',
                      pos: meData?.position
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-1 text-base font-semibold text-white/90">
                    <MetricIcon size={14} className="text-white/50" />
                    {category.formatValue({ ...meData })}
                  </div>
                  <div className="text-[10px] text-white/45 uppercase tracking-[0.12em]">{category.metricLabel}</div>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen px-6 pb-10 pt-6 text-white">
        <div className="rounded-3xl border border-white/10 bg-surface/70 p-6">
          <div className="flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <div className="h-10 w-10 rounded-full border-2 border-accent-primary/30 border-t-accent-primary animate-spin" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen px-6 pb-10 pt-6 text-white">
        <div className="rounded-3xl border border-red-500/30 bg-red-500/10 p-6 text-center">
          <div className="flex items-center justify-center">
            <AlertTriangle size={28} className="text-red-200" />
          </div>
          <div className="mt-3 text-sm leading-relaxed text-red-100/90">{error}</div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl border border-red-400/40 px-4 py-2 text-sm font-semibold text-red-100"
          >
            <RotateCw size={16} className="text-red-100" />
            {t('common.retry', { defaultValue: 'Повторить' })}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen px-6 pb-10 pt-6 text-white">
      <div className="mx-auto w-full lg:max-w-6xl">
        <div className="lg:grid lg:grid-cols-12 lg:items-start lg:gap-8">
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="mb-6 rounded-3xl border border-sky-300/20 bg-gradient-to-br from-sky-500/10 via-amber-500/5 to-emerald-500/10 overflow-hidden">
              <div className="relative w-full aspect-[5/4] bg-slate-950/30">
                <img
                  src="/images/event/poster2.jpg"
                  alt="event"
                  className="absolute inset-0 h-full w-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
              </div>

              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-white/70">
                  <Calendar size={14} className="text-white/60" />
                  {t('eventOlympus.periodText', { defaultValue: '5–19 марта' })}
                </div>
                <h1 className="mt-2 text-3xl font-black leading-tight">
                  {t('eventOlympus.title', { defaultValue: 'Штурм Олимпа' })}
                </h1>

                <p className="mt-3 text-base text-white/80">
                  {t('eventOlympus.about.reason', {
                    defaultValue:
                      'Пока весь мир смотрел на медали, мы готовили свою арену. Теперь настала твоя очередь забрать золото.'
                  })}
                </p>

                <p className="mt-3 text-base text-white/80">
                  {t('eventOlympus.description', {
                    defaultValue:
                      'Поднимайся на Олимп: играй в Королевскую битву, попадай в топы и собирай медали, скины и кристаллы!'
                  })}
                </p>

                <div className="mt-5 flex justify-center">
                  <div className="w-full max-w-sm rounded-2xl border border-accent-primary/25 bg-gradient-to-br from-accent-primary/15 via-sky-500/10 to-purple-500/10 p-4 text-center shadow-[0_0_24px_rgba(0,255,127,0.08)]">
                    {Number(secondsLeft ?? 0) > 0 && (
                      <div className="flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white/65">
                        <Timer size={14} className="text-white/60" />
                        {t('eventOlympus.timeLeftLabel', { defaultValue: 'До конца осталось' })}
                      </div>
                    )}
                    <div className="mt-2 text-3xl font-black text-white">
                      {Number(secondsLeft ?? 0) <= 0
                        ? t('eventOlympus.finished', { defaultValue: 'Штурм Олимпа завершен!' })
                        : formatCountdown(secondsLeft ?? 0)}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-surface/60 px-3 py-1.5 text-xs text-white/70">
                    <Trophy size={14} className="text-white/60" />
                    {t('eventOlympus.nominationCount', { defaultValue: '5 номинаций' })}
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-surface/60 px-3 py-1.5 text-xs text-white/70">
                    <Users size={14} className="text-white/60" />
                    {t('eventOlympus.random100.badge', { defaultValue: '115 наград' })}
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-surface/60 px-3 py-1.5 text-xs text-white/70">
                    <Gem size={14} className="text-white/60" />
                    {t('eventOlympus.random100.fund', { defaultValue: '10 000 000 фонд' })}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/royale')}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-sky-200/30 bg-gradient-to-r from-sky-500/25 via-amber-500/20 to-emerald-500/25 px-4 py-3 text-sm font-black text-white hover:border-sky-200/60"
                >
                  <Zap size={18} className="text-white" />
                  {t('eventOlympus.cta', { defaultValue: 'В Королевскую Битву' })}
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="mt-5 rounded-2xl border border-white/10 bg-surface/70 p-4 sm:p-5 lg:mt-0">
              <div className="flex items-center gap-2">
                <Trophy size={18} className="text-amber-200" />
                <h2 className="text-lg font-black">{t('eventOlympus.leaderboard.title', { defaultValue: 'Топы и номинации' })}</h2>
              </div>
            </div>

            <div className="mt-3.5 grid gap-3 lg:grid-cols-2">
              {categories.map((category) => renderCategoryCard(category))}
            </div>

            {hasRandom100 && (
              <div className="mt-6 rounded-2xl border border-emerald-300/20 bg-emerald-500/10 p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Users size={18} className="text-emerald-200" />
                    <div className="text-base font-black text-emerald-100">
                      {t('eventOlympus.random100.title', { defaultValue: '+100 случайных победителей' })}
                    </div>
                  </div>
                </div>

                <div className="mt-2 text-sm text-emerald-50/80">
                  {t('eventOlympus.random100.text', {
                    defaultValue: 'Ещё 100 случайных участников Штурма Олимпа получат по 5000 кристаллов.'
                  })}
                </div>

                <div className="mt-3 rounded-xl border border-white/10 bg-dark-primary/35 p-4 text-sm leading-relaxed text-white/80">
                  {isRandom100Participant
                    ? t('eventOlympus.random100.userParticipatesText', {
                        defaultValue: 'Ты уже участвуешь!'
                      })
                    : t('eventOlympus.random100.userNotParticipatesText', {
                        defaultValue: 'Сделай хотя бы 1 ставку в Королевской битве, чтобы участвовать в случайном розыгрыше.'
                      })}
                </div>
              </div>
            )}

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
                <div className="flex items-center gap-2">
                  <Coins size={18} className="text-amber-200" />
                  <h2 className="text-base font-black">
                    {t('eventOlympus.howTo.title', { defaultValue: 'Как принять участие' })}
                  </h2>
                </div>
                <div className="mt-3 text-sm text-white/75 leading-relaxed">
                  {t('eventOlympus.howTo.step1', { defaultValue: 'Открой Королевскую битву и сделай ставку.' })}
                </div>
                <div className="mt-2 text-sm text-white/75 leading-relaxed">
                  {t('eventOlympus.howTo.step2', { defaultValue: 'Чтобы участвовать — нужна хотя бы 1 ставка за период.' })}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
                <div className="flex items-center gap-2">
                  <Trophy size={18} className="text-purple-200" />
                  <h2 className="text-base font-black">
                    {t('eventOlympus.winnerRules.title', { defaultValue: 'Как выбираем победителей' })}
                  </h2>
                </div>
                <div className="mt-3 text-sm text-white/75 leading-relaxed">
                  {t('eventOlympus.winnerRules.text1', {
                    defaultValue: '5 номинаций, в каждой — 3 победителя.'
                  })}
                </div>
                <div className="mt-2 text-sm text-white/75 leading-relaxed">
                  {t('eventOlympus.winnerRules.text2', {
                    defaultValue: 'Плюс 100 случайных участников получат по 5000 кристаллов.'
                  })}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/royale')}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-sky-200/30 bg-gradient-to-r from-sky-500/25 via-amber-500/20 to-emerald-500/25 px-4 py-3 text-sm font-black text-white hover:border-sky-200/60"
            >
              <Zap size={18} className="text-white" />
              {t('eventOlympus.cta', { defaultValue: 'В Королевскую Битву' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EventPage
